import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { MissionSummary } from '@apos/shared';
import { Button, Chip, EmptyState, Field, Icon, Modal, TextInput } from '../../components/ui';
import { Notice } from './pageParts';
import { request, api, errorMessage } from '../../lib/api';
import { fmt } from '../../lib/fmt';
import { useServerNow } from '../../lib/clock';
import { AppHeader } from '../AppHeader';
import { PublicLink } from '../../components/PublicLink';
import { KIND_ICON, kindLabel, ROLE_LABEL, STATUS_LABEL, STATUS_TONE } from '../labels';

export function MissionsPage() {
  const [setupPending,setSetupPending]=useState(false);
  useEffect(()=>{void request<{completedAt:number|null}>('GET','/me/onboarding').then(s=>setSetupPending(!s.completedAt)).catch(()=>{});},[]);
  const [missions, setMissions] = useState<MissionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [search, setSearch] = useState('');
  const nav = useNavigate();
  const [params] = useSearchParams();
  const archived = params.get('view') === 'archived';

  useEffect(() => {
    let active = true;
    setMissions(null);setError(null);
    const load = async () => {
      try {
        const list = await (archived ? api.missions.archived() : api.missions.list());
        if (active) { setMissions(list); setError(null); }
      } catch (e) { if (active) setError(errorMessage(e)); }
    };
    void load();
    const timer = setInterval(load, 20_000);
    return () => { active = false; clearInterval(timer); };
  }, [archived]);

  const groups = useMemo(() => {
    const query=search.trim().toLowerCase();
    const list = (missions ?? []).filter(s=>!query || `${s.mission.name} ${s.mission.code} ${kindLabel(s.mission.kind)}`.toLowerCase().includes(query));
    return {
      // a running practice scenario is live now too (after the real shoots), not hidden in the folded practice list
      live: list.filter((m) => m.mission.status === 'live'),
      planned: list.filter((m) => !m.mission.simulation && m.mission.status === 'planned').sort((a, b) => a.mission.windowStart - b.mission.windowStart),
      ended: list.filter((m) => !m.mission.simulation && m.mission.status === 'ended').sort((a,b)=>(b.mission.actualEnd ?? b.mission.windowEnd)-(a.mission.actualEnd ?? a.mission.windowEnd)),
      practice: list.filter(s=>!!s.mission.simulation && s.mission.status !== 'live'),
      filtered: list,
    };
  }, [missions,search]);

  const realCount = (missions ?? []).filter((m) => !m.mission.simulation).length;
  const liveCount = groups.live.length;
  // the big empty state only when there is nothing at all to look at; a running simulation is something
  const noReal = !!missions && !archived && !search.trim() && !missions.some((s) => !s.mission.simulation);
  const showEmpty = !!missions && !search.trim() && (archived ? missions.length === 0 : noReal && liveCount === 0);

  return (
    <div className="page">
      <AppHeader />
      <main className="page__main shoots-page">
        <div className="page__head">
          <div>
            <div className="eyebrow">{archived ? 'Archive' : 'Production'}</div>
            <h1>{archived ? 'Archived shoots' : 'Shoots'}</h1>
            {missions && !archived ? (
              <p className="page__lede shoots-summary">
                {liveCount ? (
                  <span className="shoots-summary__live">
                    <span className="shoots-summary__dot" aria-hidden />
                    {liveCount} live now
                  </span>
                ) : null}
                <span>
                  {realCount === 0 ? 'No shoots planned yet' : `${groups.planned.length} planned · ${groups.ended.length} ended`}
                </span>
              </p>
            ) : null}
          </div>
          <div className="page__actions">
            <Button icon="link" onClick={() => setJoinOpen(true)}>
              Join with code
            </Button>
            <Button variant="primary" icon="plus" onClick={() => nav('/missions/new')}>
              New shoot
            </Button>
          </div>
        </div>

        {setupPending ? (
          <Notice
            className="shoots-setup"
            icon="settings"
            title="Finish setting up Airview Studio"
            action={
              <Link to="/onboarding" className="btn btn--secondary btn--md">
                <span className="btn__label">Continue setup</span>
                <Icon name="arrow-right" size={16} />
              </Link>
            }
          >
            Join your team or connect aircraft data so real aircraft show up on your shoots.
          </Notice>
        ) : null}

        <div className="shoots-layout shoots-layout--single">
          <div className="shoots-main">
            <div className="shoot-collection-bar">
              <nav className="shoot-collections" aria-label="Shoot collections">
                <Link to="/missions" aria-current={!archived ? 'page' : undefined}>
                  Active
                </Link>
                <Link to="/missions?view=archived" aria-current={archived ? 'page' : undefined}>
                  Archived
                </Link>
              </nav>
              <div className="shoot-search">
                <TextInput type="search" icon="search" aria-label="Search shoots" placeholder="Search name, code or type" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            {archived ? <p className="shoots-note">Archived shoots retain their tracks, marks, media and planning history. An owner can restore a shoot from its archived page.</p> : null}

            {error ? (
              <Notice tone="error" role="alert">
                {error}
              </Notice>
            ) : null}
            {missions === null && !error ? (
              <div className="mission-grid" aria-busy="true" aria-label="Loading shoots">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="mission-card mission-card--skeleton" aria-hidden />
                ))}
              </div>
            ) : null}

            {/* On shoot day the live mission comes first — real shoots before simulations. */}
            {!archived && groups.live.length ? (
              <MissionGroup title="Live now" featured items={[...groups.live].sort((a, b) => Number(!!a.mission.simulation) - Number(!!b.mission.simulation))} />
            ) : null}

            {showEmpty ? (
              <EmptyState
                className="shoots-empty"
                icon={archived ? 'download' : 'plan'}
                title={archived ? 'No archived shoots' : 'No shoots yet'}
                body={archived ? 'Archived shoots will appear here. Nothing has been permanently deleted.' : 'Plan your first shoot, then review aircraft and camera positions on the map.'}
                action={
                  archived ? (
                    <Link to="/missions" className="btn btn--secondary btn--md">
                      <span className="btn__label">Back to active shoots</span>
                    </Link>
                  ) : (
                    <Button icon="plus" onClick={() => nav('/missions/new')}>
                      Plan a shoot
                    </Button>
                  )
                }
              />
            ) : noReal && liveCount > 0 ? (
              <div className="shoots-inline-empty">
                <Icon name="plan" size={18} />
                <span>No real shoots planned yet. Your practice run is live above.</span>
                <Button size="sm" variant="secondary" icon="plus" onClick={() => nav('/missions/new')}>
                  Plan a shoot
                </Button>
              </div>
            ) : null}

            {!archived && groups.planned.length ? <MissionGroup title="Planned" items={groups.planned} /> : null}
            {!archived && groups.practice.length ? <MissionGroup title="Saved practice records" items={groups.practice} /> : null}
            {!archived && groups.ended.length ? <MissionGroup title="Ended" items={groups.ended} /> : null}
            {archived && groups.filtered.length ? <MissionGroup title="Archived" items={groups.filtered} /> : null}
            {missions && search.trim() && groups.filtered.length === 0 ? (
              <EmptyState icon="search" title="No matching shoots" body="Try a name, slate code or shoot type." action={<Button variant="secondary" onClick={() => setSearch('')}>Clear search</Button>} />
            ) : null}
          </div>
        </div>
      </main>
      <JoinModal open={joinOpen} onClose={() => setJoinOpen(false)} />
    </div>
  );
}

function MissionGroup({ title, items, featured }: { title: string; items: MissionSummary[]; featured?: boolean }) {
  return (
    <section className={featured ? 'mission-group mission-group--featured' : 'mission-group'}>
      <h2 className="mission-group__title">
        {title} <span className="num">{items.length}</span>
      </h2>
      <div className="mission-grid">
        {items.map((s) => (
          <MissionCard key={s.mission.id} s={s} />
        ))}
      </div>
    </section>
  );
}

function MissionCard({ s }: { s: MissionSummary }) {
  const m = s.mission;
  const now = useServerNow(m.status === 'live' ? 1000 : 60_000);
  const to = `/m/${m.id}` as const;
  const start = m.actualStart ?? m.windowStart;
  const end = m.actualEnd ?? m.windowEnd;
  let timing: string;
  if (m.status === 'live') timing = `T+ ${fmt.duration(Math.max(0, (now - start) / 1000))}`;
  else if (m.status === 'planned') { const days=Math.ceil((m.windowStart-now)/86400000); timing=m.windowStart>now ? days>1 ? `In ${days} days` : `In ${fmt.duration((m.windowStart-now)/1000)}` : now<=m.windowEnd ? 'Scheduled window open' : 'Scheduled window passed'; }
  else timing = `Ended ${fmt.local(end, m.timezone)}`;
  return (
    <PublicLink to={to} className={`mission-card mission-card--${m.status}`}>
      <div className="mission-card__top">
        <span className="mission-card__code num">{m.code}</span>
        <Chip tone={STATUS_TONE[m.status]} dot={m.status === 'live'} size="sm">
          {m.archivedAt != null ? 'ARCHIVED' : STATUS_LABEL[m.status]}
        </Chip>
      </div>
      <div className="mission-card__name">{m.name}</div>
      <div className="mission-card__kind">
        <Icon name={KIND_ICON[m.kind]} size={14} />
        {kindLabel(m.kind)}
        {m.simulation ? (
          <Chip size="sm" tone="neutral" icon="sim">
            SIM
          </Chip>
        ) : null}
      </div>
      <div className="mission-card__window">
        <div>
          <span className="eyebrow">
            {fmt.date(start, m.timezone)}
            {fmt.date(end, m.timezone) !== fmt.date(start, m.timezone) && end - start > 24 * 3600_000 ? ` – ${fmt.date(end, m.timezone)}` : ''}
          </span>
          <span className="num">
            {fmt.local(start, m.timezone, false)} – {fmt.local(end, m.timezone, false).replace(/ \w+$/, '')}
          </span>
        </div>
        <div>
          <span className="eyebrow">UTC</span>
          <span className="num">
            {fmt.utc(start, false)} – {fmt.utc(end, false)}
          </span>
        </div>
      </div>
      <div className="mission-card__foot">
        <span className="num">{timing}</span>
        <span className="mission-card__meta">
          <span>
            <Icon name="aircraft" size={13} />
            <span className="num">{s.aircraftCount}</span>
          </span>
          <span>
            <Icon name="users" size={13} />
            <span className="num">{s.memberCount}</span>
          </span>
          <span>{ROLE_LABEL[s.myRole]}</span>
        </span>
      </div>
    </PublicLink>
  );
}

function JoinModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const nav = useNavigate();
  const [code, setCode] = useState('');
  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Join"
      title="Join a shoot"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            iconRight="arrow-right"
            disabled={!code.trim()}
            onClick={() => {
              onClose();
              nav(`/join/${encodeURIComponent(code.trim())}`);
            }}
          >
            Continue
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) {
            onClose();
            nav(`/join/${encodeURIComponent(code.trim())}`);
          }
        }}
      >
        <Field label="Invite code" hint="The 8-character code from the shoot’s producer. Dashes and spaces are ignored.">
          {(id) => (
            <TextInput
              id={id}
              mono
              className="input--code"
              autoFocus
              autoCapitalize="characters"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
